/**
 * Upgrade Plan Page
 *
 * Displays all available subscription plans. Clicking a plan shows its terms.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Shield,
  Users,
  FileText,
  Briefcase,
  ArrowRight,
  Check,
  Sparkles,
} from 'lucide-react';

import {
  Button,
  Badge,
  Card,
  CardBody,
  Loader,
  Modal,
} from '@/components/common';
import { subscriptionService, organizationService } from '@/api/services';
import type { PlanListApiItem } from '@/api/services/subscription.service';
import type { OrganizationPlan } from '@/api/types';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';

export function UpgradePlanPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [plans, setPlans] = useState<PlanListApiItem[]>([]);
  const [currentPlan, setCurrentPlan] = useState<OrganizationPlan | null>(null);
  const [allowedPlanIds, setAllowedPlanIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<PlanListApiItem | null>(null);
  const [confirmTerm, setConfirmTerm] = useState<PlanListApiItem['planTerms'][number] | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleUpgrade = async () => {
    if (!confirmTerm || !selectedPlan || !id) return;
    setIsUpgrading(true);
    try {
      await subscriptionService.upgradeSubscriptionPlan({
        orgId: id,
        subscriptionPlanCode: selectedPlan.subscriptionPlan.code,
        planTermCode: confirmTerm.planTermCode,
      });
      toast.success('Plan upgraded successfully');
      setConfirmTerm(null);
      navigate(`/organizations/${id}`);
    } catch (error) {
      console.error('Failed to upgrade plan:', error);
      toast.error('Failed to upgrade plan');
    } finally {
      setIsUpgrading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [planList, orgPlan, allowedPlans] = await Promise.all([
          subscriptionService.getSubscriptionPlanList(),
          id ? organizationService.getOrganizationPlan(id) : Promise.resolve([]),
          user?.id
            ? subscriptionService.getAllowedPlansByUserId(user.id)
            : Promise.resolve([]),
        ]);
        setPlans(planList);
        setCurrentPlan(orgPlan[0] || null);
        setAllowedPlanIds(
          new Set(allowedPlans.map((p) => p.subscriptionPlan.id))
        );
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        toast.error('Failed to load subscription plans');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader size="lg" message="Loading subscription plans..." />
      </div>
    );
  }

  // Plan terms view
  if (selectedPlan) {
    const sp = selectedPlan.subscriptionPlan;
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedPlan(null)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
              {sp.name}
            </h1>
            <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
              Select a term to upgrade
            </p>
          </div>
        </div>

        {/* Plan details summary */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-4 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground dark:text-foreground-dark">{sp.name}</h2>
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">{sp.description}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
                <span className="text-foreground-muted dark:text-foreground-dark-muted">Employees:</span>
                <span className="font-semibold text-foreground dark:text-foreground-dark">{sp.maxEmployees}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
                <span className="text-foreground-muted dark:text-foreground-dark-muted">Contracts:</span>
                <span className="font-semibold text-foreground dark:text-foreground-dark">{sp.maxContracts}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Briefcase className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
                <span className="text-foreground-muted dark:text-foreground-dark-muted">Active:</span>
                <span className="font-semibold text-foreground dark:text-foreground-dark">{sp.maxActiveContracts}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
                <span className="text-foreground-muted dark:text-foreground-dark-muted">Users:</span>
                <span className="font-semibold text-foreground dark:text-foreground-dark">{sp.maxUsers}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Plan Terms */}
        {selectedPlan.planTerms.length === 0 ? (
          <div className="py-8 text-center">
            <CreditCard className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
            <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
              No terms available for this plan
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {selectedPlan.planTerms.map((term) => (
              <Card key={term.id}>
                <CardBody className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-foreground dark:text-foreground-dark">
                      {term.termType}
                    </h3>
                    {term.isActive && (
                      <Badge variant="success" dot>Active</Badge>
                    )}
                  </div>
                  <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted mb-4">
                    Duration: {term.termValue} {term.termType.toLowerCase()}
                  </p>
                  <div className="space-y-2">
                    {term.actualPrice > term.price && (
                      <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted line-through">
                        ${term.actualPrice}
                      </p>
                    )}
                    <p className="text-3xl font-bold text-foreground dark:text-foreground-dark">
                      ${term.price}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    className="mt-4 w-full"
                    disabled={!term.isActive}
                    onClick={() => setConfirmTerm(term)}
                  >
                    Select Term
                  </Button>
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {/* Upgrade Confirmation Modal */}
        <Modal
          open={!!confirmTerm}
          onOpenChange={(open) => !open && setConfirmTerm(null)}
          title="Confirm Plan Upgrade"
          description="Please review the details before proceeding."
          size="sm"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setConfirmTerm(null)}
                disabled={isUpgrading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUpgrade}
                disabled={isUpgrading}
              >
                {isUpgrading ? 'Upgrading...' : 'Yes, Upgrade'}
              </Button>
            </>
          }
        >
          {confirmTerm && (
            <div className="space-y-3">
              <div className="rounded-lg bg-primary/5 p-4">
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Plan</p>
                <p className="text-lg font-semibold text-foreground dark:text-foreground-dark">{sp.name}</p>
              </div>
              <div className="rounded-lg bg-primary/5 p-4">
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Term</p>
                <p className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                  {confirmTerm.termType} &mdash; {confirmTerm.termValue} {confirmTerm.termType.toLowerCase()}
                </p>
              </div>
              <div className="rounded-lg bg-primary/5 p-4">
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">Price</p>
                <p className="text-lg font-semibold text-foreground dark:text-foreground-dark">${confirmTerm.price}</p>
              </div>
              <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Are you sure you want to upgrade to this plan? This action will take effect immediately.
              </p>
            </div>
          )}
        </Modal>
      </div>
    );
  }

  // Plans list view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/organizations/${id}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Upgrade Plan
          </h1>
          <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Select a subscription plan to view its terms
          </p>
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="py-12 text-center">
          <CreditCard className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
          <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
            No subscription plans available
          </p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((item) => {
            const sp = item.subscriptionPlan;
            const isCurrentPlan = currentPlan?.name === sp.name;
            const isAllowed = allowedPlanIds.size === 0 || allowedPlanIds.has(sp.id);
            return (
              <div
                key={sp.id}
                onClick={() => isAllowed && setSelectedPlan(item)}
                className={`group relative rounded-xl border-2 bg-card dark:bg-card-dark p-6 transition-all duration-200 ${
                  !isAllowed
                    ? 'cursor-not-allowed opacity-60 blur-[0.5px]'
                    : 'cursor-pointer hover:-translate-y-1 hover:shadow-lg'
                } ${
                  isCurrentPlan
                    ? 'border-primary shadow-md shadow-primary/10'
                    : isAllowed
                      ? 'border-border dark:border-border-dark hover:border-primary/50'
                      : 'border-border dark:border-border-dark'
                }`}
              >
                {/* Current plan indicator */}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white shadow-sm">
                      <Sparkles className="h-3 w-3" />
                      Current Plan
                    </span>
                  </div>
                )}

                {/* Plan icon & name */}
                <div className="flex items-start gap-4 mt-1">
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                    isCurrentPlan
                      ? 'bg-primary text-white'
                      : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white'
                  } transition-colors`}>
                    <Shield className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-foreground dark:text-foreground-dark">
                      {sp.name}
                    </h3>
                    <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted line-clamp-2">
                      {sp.description}
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div className="my-5 border-t border-border dark:border-border-dark" />

                {/* Features list */}
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/10">
                      <Check className="h-3.5 w-3.5 text-success" />
                    </span>
                    <span className="text-foreground-muted dark:text-foreground-dark-muted">Up to</span>
                    <span className="ml-auto font-semibold text-foreground dark:text-foreground-dark">{sp.maxEmployees} Employees</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/10">
                      <Check className="h-3.5 w-3.5 text-success" />
                    </span>
                    <span className="text-foreground-muted dark:text-foreground-dark-muted">Up to</span>
                    <span className="ml-auto font-semibold text-foreground dark:text-foreground-dark">{sp.maxContracts} Contracts</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/10">
                      <Check className="h-3.5 w-3.5 text-success" />
                    </span>
                    <span className="text-foreground-muted dark:text-foreground-dark-muted">Up to</span>
                    <span className="ml-auto font-semibold text-foreground dark:text-foreground-dark">{sp.maxActiveContracts} Active</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/10">
                      <Check className="h-3.5 w-3.5 text-success" />
                    </span>
                    <span className="text-foreground-muted dark:text-foreground-dark-muted">Up to</span>
                    <span className="ml-auto font-semibold text-foreground dark:text-foreground-dark">{sp.maxUsers} Users</span>
                  </li>
                </ul>

                {/* CTA */}
                <button
                  disabled={!isAllowed}
                  className={`mt-6 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                    !isAllowed
                      ? 'bg-secondary-100 text-foreground-muted cursor-not-allowed dark:bg-secondary-800 dark:text-foreground-dark-muted'
                      : isCurrentPlan
                        ? 'bg-primary/10 text-primary'
                        : 'bg-secondary-100 text-foreground dark:bg-secondary-800 dark:text-foreground-dark group-hover:bg-primary group-hover:text-white'
                  }`}
                >
                  {!isAllowed ? 'Not Available' : isCurrentPlan ? 'View Terms' : 'Select Plan'}
                  {isAllowed && <ArrowRight className="h-4 w-4" />}
                </button>

                {item.planTerms.length > 0 && (
                  <p className="mt-3 text-center text-xs text-foreground-muted dark:text-foreground-dark-muted">
                    {item.planTerms.length} billing term{item.planTerms.length > 1 ? 's' : ''} available
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UpgradePlanPage;
